import { musicalStoriesData } from '../data/musicalStories';
import { styleStoriesData } from '../data/styleStories';
import { voiceStoriesData } from '../data/voiceStories';

export interface PublicStoryUrlData {
  storyId: string;
  userId: string;
  chapterIds?: string;
  title: string;
}

const regularStoriesData = [
  ...Object.values(styleStoriesData).flat(),
  ...Object.values(voiceStoriesData).flat(),
];

const normalizeChapterIds = (chapterIds?: string | string[]): string | undefined => {
  if (Array.isArray(chapterIds)) return chapterIds.join(',');
  return chapterIds;
};

const toPublicStoryUrlData = (story: {
  storyId: string;
  userId: string;
  chapterIds?: string | string[];
  title: string;
}): PublicStoryUrlData => ({
  storyId: story.storyId,
  userId: story.userId,
  chapterIds: normalizeChapterIds(story.chapterIds),
  title: story.title,
});

// Utility function to convert story titles to SEO-friendly slugs
export const createStorySlug = (title: string): string => {
  // Create SEO-friendly keywords based on story content
  const seoKeywords = {
    "Benny's Magical Bluebird Adventure": "stories-for-kids-bluebird-adventure",
    "Brave Knight Charls in Asgard": "ai-story-about-knight-adventure",
    "Milan and the Magical Chess Match": "stories-for-kids-chess-adventure",
    "Liam's Monkey Adventure": "ai-story-about-monkey-adventure",
    "Morne the Olympic Archer": "ai-story-about-an-archer",
    "Lucky Learns a New Spell": "stories-for-kids-magic-spells",
    "Sam and the Magical Ladybug": "ai-story-about-ladybug-adventure",
    "Amelia's Cooking Adventure": "stories-for-kids-cooking-adventure",
    "The Scribble Hero": "the-scribble-hero",
    "Pixel Princess Quest": "pixel-princess-quest",
    "The Cloud Painter": "the-cloud-painter",
    "Super Sloth Saves the Day": "super-sloth-saves-the-day",
    "Watch the Magic Unfold": "watch-the-magic-unfold",
    "The Bubblegum Dragon": "the-bubblegum-dragon",
    "The Rainbow Umbrella": "the-rainbow-umbrella",
    "Banana Bandit vs. Captain Peel": "banana-bandit-vs-captain-peel",
    "Time-Traveling Toast": "time-traveling-toast",
    "Build-a-Castle Race": "build-a-castle-race"
  };

  // Use predefined SEO-friendly slug if available, otherwise generate from title
  if (seoKeywords[title as keyof typeof seoKeywords]) {
    return seoKeywords[title as keyof typeof seoKeywords];
  }

  // Fallback: convert title to URL-friendly format
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

// Story data mapping for SEO URLs
export const getStoryDataFromSlug = (slug: string): PublicStoryUrlData | null => {
  const storyDataMap: { [key: string]: PublicStoryUrlData } = {
    // Benny's story variations
    "stories-for-kids-bluebird-adventure": {
      storyId: "5ad594b8-2a6c-48d4-8104-4e9c44da7672",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Benny's Magical Bluebird Adventure"
    },
    "ai-story-about-bluebird": {
      storyId: "5ad594b8-2a6c-48d4-8104-4e9c44da7672",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Benny's Magical Bluebird Adventure"
    },
    "magical-stories-for-children-bluebird": {
      storyId: "5ad594b8-2a6c-48d4-8104-4e9c44da7672",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Benny's Magical Bluebird Adventure"
    },
    "bedtime-stories-for-kids-bluebird": {
      storyId: "5ad594b8-2a6c-48d4-8104-4e9c44da7672",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Benny's Magical Bluebird Adventure"
    },
    
    // Charls' story variations
    "ai-story-about-knight-adventure": {
      storyId: "8b68294f-a1ed-4977-9f0b-181acb9ca4b8",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Brave Knight Charls in Asgard"
    },
    "knight-stories-for-kids": {
      storyId: "8b68294f-a1ed-4977-9f0b-181acb9ca4b8",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Brave Knight Charls in Asgard"
    },
    "adventure-stories-for-children-knight": {
      storyId: "8b68294f-a1ed-4977-9f0b-181acb9ca4b8",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Brave Knight Charls in Asgard"
    },
    "fantasy-stories-for-kids-knight": {
      storyId: "8b68294f-a1ed-4977-9f0b-181acb9ca4b8",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Brave Knight Charls in Asgard"
    },
    
    // Milan's story variations
    "stories-for-kids-chess-adventure": {
      storyId: "5b1bac27-de72-4355-aed8-f02127b4e2e6",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Milan and the Magical Chess Match"
    },
    "educational-stories-for-children-chess": {
      storyId: "5b1bac27-de72-4355-aed8-f02127b4e2e6",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Milan and the Magical Chess Match"
    },
    "ai-story-about-chess-match": {
      storyId: "5b1bac27-de72-4355-aed8-f02127b4e2e6",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Milan and the Magical Chess Match"
    },
    "learning-stories-for-kids-chess": {
      storyId: "5b1bac27-de72-4355-aed8-f02127b4e2e6",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Milan and the Magical Chess Match"
    },
    
    // Liam's story variations
    "ai-story-about-monkey-adventure": {
      storyId: "900a4a2a-b4fb-4b7e-978e-70b0a8eed27d",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Liam's Monkey Adventure"
    },
    "monkey-stories-for-kids": {
      storyId: "900a4a2a-b4fb-4b7e-978e-70b0a8eed27d",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Liam's Monkey Adventure"
    },
    "jungle-stories-for-children": {
      storyId: "900a4a2a-b4fb-4b7e-978e-70b0a8eed27d",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Liam's Monkey Adventure"
    },
    "animal-stories-for-kids-monkey": {
      storyId: "900a4a2a-b4fb-4b7e-978e-70b0a8eed27d",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Liam's Monkey Adventure"
    },
    
    // Morne's story variations
    "ai-story-about-an-archer": {
      storyId: "ef3fc038-c056-4236-865d-fe8b8917252f",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Morne the Olympic Archer"
    },
    "archer-stories-for-kids": {
      storyId: "ef3fc038-c056-4236-865d-fe8b8917252f",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Morne the Olympic Archer"
    },
    "olympic-stories-for-children": {
      storyId: "ef3fc038-c056-4236-865d-fe8b8917252f",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Morne the Olympic Archer"
    },
    "sports-stories-for-kids-archer": {
      storyId: "ef3fc038-c056-4236-865d-fe8b8917252f",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Morne the Olympic Archer"
    },
    
    // Lucky's story variations
    "stories-for-kids-magic-spells": {
      storyId: "385fc89a-55f0-402d-911b-99cce6514e4a",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Lucky Learns a New Spell"
    },
    "magical-stories-for-children-spells": {
      storyId: "385fc89a-55f0-402d-911b-99cce6514e4a",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Lucky Learns a New Spell"
    },
    "wizard-stories-for-kids": {
      storyId: "385fc89a-55f0-402d-911b-99cce6514e4a",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Lucky Learns a New Spell"
    },
    "fantasy-stories-for-kids-magic": {
      storyId: "385fc89a-55f0-402d-911b-99cce6514e4a",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Lucky Learns a New Spell"
    },
    
    // Sam's story variations
    "ai-story-about-ladybug-adventure": {
      storyId: "59a20d91-61ea-4a9d-a5c5-a4bd6d7f7722",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Sam and the Magical Ladybug"
    },
    "ladybug-stories-for-kids": {
      storyId: "59a20d91-61ea-4a9d-a5c5-a4bd6d7f7722",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Sam and the Magical Ladybug"
    },
    "insect-stories-for-children": {
      storyId: "59a20d91-61ea-4a9d-a5c5-a4bd6d7f7722",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Sam and the Magical Ladybug"
    },
    "nature-stories-for-kids-ladybug": {
      storyId: "59a20d91-61ea-4a9d-a5c5-a4bd6d7f7722",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Sam and the Magical Ladybug"
    },
    
    // Amelia's story variations
    "stories-for-kids-cooking-adventure": {
      storyId: "5e90f39f-c55f-429f-b2de-ffc8ba2c1373",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Amelia's Cooking Adventure"
    },
    "cooking-stories-for-children": {
      storyId: "5e90f39f-c55f-429f-b2de-ffc8ba2c1373",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Amelia's Cooking Adventure"
    },
    "kitchen-stories-for-kids": {
      storyId: "5e90f39f-c55f-429f-b2de-ffc8ba2c1373",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Amelia's Cooking Adventure"
    },
    "food-stories-for-children-cooking": {
      storyId: "5e90f39f-c55f-429f-b2de-ffc8ba2c1373",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Amelia's Cooking Adventure"
    },
    "watch-the-magic-unfold": {
      storyId: "e3edb288-65f8-4ce5-8ce3-c0bafca2309b",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "",
      title: "Watch the Magic Unfold"
    },
    "the-scribble-hero": {
      storyId: "f9feef23-5a22-43b1-a5fc-e3123bbc59b7",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "f9feef23-5a22-43b1-a5fc-e3123bbc59b7",
      title: "The Scribble Hero"
    },
    "pixel-princess-quest": {
      storyId: "b44cbff0-9e26-4e1a-9589-8da70bd2b75d",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "b44cbff0-9e26-4e1a-9589-8da70bd2b75d",
      title: "Pixel Princess Quest"
    },
    "the-cloud-painter": {
      storyId: "9ea32394-44c8-4abf-9946-3caf9c796f89",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "9ea32394-44c8-4abf-9946-3caf9c796f89",
      title: "The Cloud Painter"
    },
    "super-sloth-saves-the-day": {
      storyId: "23238420-764d-4ccf-a9aa-808e050096f5",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "23238420-764d-4ccf-a9aa-808e050096f5",
      title: "Super Sloth Saves the Day"
    },
    "the-bubblegum-dragon": {
      storyId: "bbbd95c6-3b86-43bc-9062-3abad0c6e119",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "bbbd95c6-3b86-43bc-9062-3abad0c6e119",
      title: "The Bubblegum Dragon"
    },
    "the-rainbow-umbrella": {
      storyId: "241cdf7a-ed86-4173-ab6b-dce0d9f865cc",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "241cdf7a-ed86-4173-ab6b-dce0d9f865cc",
      title: "The Rainbow Umbrella"
    },
    "banana-bandit-vs-captain-peel": {
      storyId: "d1151fb7-fd68-435c-8e51-b51eab7c8ebc",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "d1151fb7-fd68-435c-8e51-b51eab7c8ebc",
      title: "Banana Bandit vs. Captain Peel"
    },
    "time-traveling-toast": {
      storyId: "11b2d0ee-5100-4a91-9f7f-42052513dded",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "11b2d0ee-5100-4a91-9f7f-42052513dded",
      title: "Time-Traveling Toast"
    },
    "build-a-castle-race": {
      storyId: "ae144c4d-3954-4284-a08d-79a28d640b9f",
      userId: "d46046ba-8cb0-45be-b338-5f6f98b6a223",
      chapterIds: "ae144c4d-3954-4284-a08d-79a28d640b9f",
      title: "Build-a-Castle Race"
    }
  };

  const mappedStory = storyDataMap[slug];
  if (mappedStory) return mappedStory;

  // Keep old title-only URLs working for every regular showcase story. New
  // links include the story ID as well, so titles that happen to share a slug
  // are no longer ambiguous.
  const regularStory = regularStoriesData.find(
    story => createStorySlug(story.title) === slug
  );

  if (regularStory) return toPublicStoryUrlData(regularStory);

  const musicalStory = musicalStoriesData.find(
    story => createStorySlug(story.title) === slug
  );

  if (!musicalStory) return null;

  return toPublicStoryUrlData(musicalStory);
};

// Resolve the exact public story selected by a card/detail page. The ID is the
// durable part of the URL; the title slug is descriptive and can safely change.
export const getStoryDataFromId = (storyId: string): PublicStoryUrlData | null => {
  const regularStory = regularStoriesData.find(story => story.storyId === storyId);
  if (regularStory) return toPublicStoryUrlData(regularStory);

  const musicalStory = musicalStoriesData.find(story => story.storyId === storyId);
  return musicalStory ? toPublicStoryUrlData(musicalStory) : null;
};

// Canonical story URLs for sitemap (only one slug per story)
export const getAdditionalStoryUrls = () => {
  return [
    "stories-for-kids-bluebird-adventure",
    "ai-story-about-knight-adventure",
    "stories-for-kids-chess-adventure",
    "ai-story-about-monkey-adventure",
    "ai-story-about-an-archer",
    "stories-for-kids-magic-spells",
    "ai-story-about-ladybug-adventure",
    "stories-for-kids-cooking-adventure",
    "the-scribble-hero",
    "pixel-princess-quest",
    "the-cloud-painter",
    "super-sloth-saves-the-day",
    "watch-the-magic-unfold",
    "the-bubblegum-dragon",
    "the-rainbow-umbrella",
    "banana-bandit-vs-captain-peel",
    "time-traveling-toast",
    "build-a-castle-race",
  ];
};

// Canonical slug mapping: all slug variants → single canonical slug per story.
// This prevents duplicate content from multiple slugs pointing to the same story.
export const CANONICAL_STORY_SLUGS: Record<string, string> = {
  // Benny's story
  'ai-story-about-bluebird': 'stories-for-kids-bluebird-adventure',
  'magical-stories-for-children-bluebird': 'stories-for-kids-bluebird-adventure',
  'bedtime-stories-for-kids-bluebird': 'stories-for-kids-bluebird-adventure',
  // Charls' story
  'knight-stories-for-kids': 'ai-story-about-knight-adventure',
  'adventure-stories-for-children-knight': 'ai-story-about-knight-adventure',
  'fantasy-stories-for-kids-knight': 'ai-story-about-knight-adventure',
  // Milan's story
  'educational-stories-for-children-chess': 'stories-for-kids-chess-adventure',
  'ai-story-about-chess-match': 'stories-for-kids-chess-adventure',
  'learning-stories-for-kids-chess': 'stories-for-kids-chess-adventure',
  // Liam's story
  'monkey-stories-for-kids': 'ai-story-about-monkey-adventure',
  'jungle-stories-for-children': 'ai-story-about-monkey-adventure',
  'animal-stories-for-kids-monkey': 'ai-story-about-monkey-adventure',
  // Morne's story
  'archer-stories-for-kids': 'ai-story-about-an-archer',
  'olympic-stories-for-children': 'ai-story-about-an-archer',
  'sports-stories-for-kids-archer': 'ai-story-about-an-archer',
  // Lucky's story
  'magical-stories-for-children-spells': 'stories-for-kids-magic-spells',
  'wizard-stories-for-kids': 'stories-for-kids-magic-spells',
  'fantasy-stories-for-kids-magic': 'stories-for-kids-magic-spells',
  // Sam's story
  'ladybug-stories-for-kids': 'ai-story-about-ladybug-adventure',
  'insect-stories-for-children': 'ai-story-about-ladybug-adventure',
  'nature-stories-for-kids-ladybug': 'ai-story-about-ladybug-adventure',
  // Amelia's story
  'cooking-stories-for-children': 'stories-for-kids-cooking-adventure',
  'kitchen-stories-for-kids': 'stories-for-kids-cooking-adventure',
  'food-stories-for-children-cooking': 'stories-for-kids-cooking-adventure',
};

// Get canonical slug for a story URL slug
export const getCanonicalStorySlug = (slug: string): string => {
  return CANONICAL_STORY_SLUGS[slug] || slug;
};

// Utility function to create story URL (clean, no query params)
export const createStoryUrl = (title: string, storyId: string, userId: string, chapterIds?: string, fromHomepage?: boolean): string => {
  const slug = createStorySlug(title);
  return `/story/${slug}/${storyId}`;
};

// Utility function to create FAQ question URL
export const createFaqUrl = (question: string): string => {
  const slug = question
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return `/faq/${slug}`;
};

// Utility function to create musical slug
export const createMusicalSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

// Utility function to create musical detail URL
export const createMusicalUrl = (title: string, storyId: string): string => {
  const slug = createMusicalSlug(title);
  return `/musicals/${slug}/${storyId}`;
}; 
